import { configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import authReducer from "./slices/authSlice";
import searchReducer from "./slices/searchSlice";
import folderReducer from "./slices/folderSlice";
import baseRequirementsReducer from "./slices/baseRequirementsSlice";
import requirementsReducer from "./slices/requirementsSlice";
import templatesReducer from "./slices/templatesSlice";
import vaultReducer from "./slices/vaultSlice";
import usersReducer from "./slices/usersSlice";

/**
 * Sentry Redux Enhancer
 *
 * Captures Redux actions and state for error context.
 * Filters sensitive data before sending to Sentry.
 */
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Transform actions to remove sensitive data
  actionTransformer: (action) => {
    // Filter token from login fulfilled response
    if (action.type === "auth/onLogin/fulfilled") {
      return {
        ...action,
        payload:
          action.payload && typeof action.payload === "object"
            ? { ...action.payload, token: "[FILTERED]" }
            : action.payload,
      };
    }

    // Filter password from login request (pending action)
    if (action.type === "auth/onLogin/pending" && action.meta?.arg) {
      return {
        ...action,
        meta: {
          ...action.meta,
          arg: { ...action.meta.arg, password: "[FILTERED]" },
        },
      };
    }

    // Filter JWT token from vault actions
    if (action.type === "vault/generateJWT/fulfilled" && action.payload) {
      return {
        ...action,
        payload:
          typeof action.payload === "object"
            ? { ...action.payload, jwt_token: "[FILTERED]" }
            : "[FILTERED]",
      };
    }

    // Filter password from registration request
    if (action.type === "auth/onRegister/pending" && action.meta?.arg) {
      return {
        ...action,
        meta: {
          ...action.meta,
          arg: { ...action.meta.arg, password: "[FILTERED]" },
        },
      };
    }

    // Filter passwords from change password request
    if (action.type === "auth/onChangePassword/pending" && action.meta?.arg) {
      return {
        ...action,
        meta: {
          ...action.meta,
          arg: {
            ...action.meta.arg,
            current_password: "[FILTERED]",
            new_password: "[FILTERED]",
            confirm_new_password: "[FILTERED]",
          },
        },
      };
    }

    return action;
  },

  // Transform state to remove sensitive data
  stateTransformer: (state) => {
    return {
      ...state,
      auth: {
        ...state.auth,
        token: state.auth.token ? "[FILTERED]" : undefined,
        // Keep userInfo but filter sensitive parts if needed
        userInfo: state.auth.userInfo
          ? {
              id: state.auth.userInfo.id,
              name: state.auth.userInfo.name,
              groups: state.auth.userInfo.groups,
              is_validated: state.auth.userInfo.is_validated,
              // Exclude email for privacy
            }
          : null,
      },
      vault: {
        ...state.vault,
        jwtToken: state.vault.jwtToken ? "[FILTERED]" : null,
      },
    };
  },

  // Configure scope with useful state metadata
  configureScopeWithState: (scope, state) => {
    // Add context about the current app state
    scope.setContext("app_state", {
      is_authenticated: !!state.auth.token,
      is_validated: state.auth.userInfo?.is_validated || false,
      user_groups: state.auth.userInfo?.groups || [],
      folder_count: state.folder.count || 0,
      has_active_vault: !!state.vault.activeVault,
      has_active_requirement: !!state.vault.activeRequirement,
    });

    // Set user info if available
    if (state.auth.userInfo) {
      scope.setTag("user_validated", String(state.auth.userInfo.is_validated));
      scope.setTag(
        "user_role",
        state.auth.userInfo.groups?.[0] || "no_role",
      );
    }
  },
});

export const store = configureStore({
  reducer: {
    auth: authReducer,
    search: searchReducer,
    folder: folderReducer,
    baseRequirements: baseRequirementsReducer,
    requirements: requirementsReducer,
    templates: templatesReducer,
    vault: vaultReducer,
    user: usersReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer),
});

export type AppStore = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
